import cuid from "cuid";
import { defaultsDeep, defaultTo, get, head, isEmpty } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useCallback, useState } from "react";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { GENERATE_SUBSCRIPTION_DOCUMENTS } from "utils/api/graphql/mutations/attachment";
import { UPDATE_SUBSCRIPTION } from "utils/api/graphql/mutations/subscription";
import { SUBSCRIPTION } from "utils/api/graphql/queries/subscription";
import { BANK_ACCOUNT_OWNER_TYPE, KEYS, PAYMENT_TYPES } from "utils/constants";
import Loader from "../Components/Loader";
import Navbar from "../Components/Navbar/Navbar";
import View from "./View";

const BankInformationFamilyCoverage = ({
  project,
  onNext,
  onBack,
  updateUrl,
  currentStatus,
  tracer,
}) => {
  const [generateSubscriptionDocuments, { loading: generateLoading }] =
    useMutation(GENERATE_SUBSCRIPTION_DOCUMENTS);
  const [initialValues, setInitialValues] = useState(null);
  const subscriptionId =
    localStorage.getItem(KEYS.SUBSCRIPTION_ID) ||
    get(project, "fields.currentSubscription");

  const [updateSubscription] = useMutation(UPDATE_SUBSCRIPTION, {
    refetchQueries: [
      {
        query: SUBSCRIPTION,
        awaitRefetchQueries: true,
        variables: { where: { id: subscriptionId } },
      },
    ],
  });

  const initSubscription = useCallback(({ subscription: data }) => {
    const subscription = omitDeep(data, "__typename");

    const shouldBeAdherent =
      get(subscription, "payment.payer.owner.isAdherent", true) ||
      get(subscription, "insurancePlan.fields.payment.shouldBeAdherent", false);

    const isPayerReceiver = isEmpty(get(subscription, "payment.receiver.id"))
      ? true
      : get(subscription, "payment.receiver.id") ===
        get(subscription, "payment.payer.id");

    const payer = {
      id: cuid(),
      IBAN: null,
      BIC: null,
      ...subscription.payment?.payer,
      owner: {
        id: cuid(),
        ...subscription.payment?.payer?.owner,
        firstName: defaultTo(
          subscription.payment?.payer?.owner?.firstName,
          shouldBeAdherent
            ? get(subscription, "project.contact.user.firstname")
            : null,
        ),
        lastName: defaultTo(
          subscription.payment?.payer?.owner?.lastName,
          shouldBeAdherent
            ? get(subscription, "project.contact.user.lastname")
            : null,
        ),
        isAdherent: defaultTo(
          subscription.payment?.payer?.owner?.isAdherent,
          shouldBeAdherent,
        ),
        type: get(subscription, "insurancePlan.fields.tns")
          ? BANK_ACCOUNT_OWNER_TYPE.COMPANY
          : BANK_ACCOUNT_OWNER_TYPE.PARTICULAR,
      },
    };

    const receiver = isPayerReceiver
      ? { ...payer, owner: { ...payer.owner } }
      : defaultsDeep(subscription.payment?.receiver, {
          id: cuid(),
          IBAN: null,
          BIC: null,
          owner: {
            id: cuid(),
            isAdherent: false,
          },
        });

    const initialValues = {
      isPayerReceiver,
      payment: {
        id: cuid(),
        type:
          head(get(subscription, "insurancePlan.fields.payment.type")) ||
          PAYMENT_TYPES.DEBIT,
        frequency: null,
        debitDay: "12",
        contact: get(subscription, "project.contact"),
        ...subscription.payment,
        payer,
        receiver,
      },
    };

    setInitialValues(initialValues);
  }, []);

  const { data: subscription, loading } = useQuery(SUBSCRIPTION, {
    variables: { where: { id: subscriptionId } },
    onCompleted: initSubscription,
  });

  const data = omitDeep(subscription, "__typename");

  const insurancePlanId = get(data, "subscription.insurancePlan.id");
  const hasOptions = !isEmpty(
    get(project, `fields.upsells.${insurancePlanId}`, []),
  );
  const showSepa = hasOptions;

  const onSubmit = (values) => {
    const payment = omitDeep(values.payment, ["__typename", "contact"]);
    if (!showSepa) {
      payment.frequency = null;
      payment.debitDay = null;
    }
    updateSubscription({
      variables: {
        where: {
          id: subscriptionId,
        },
        data: {
          payment: omitDeep(payment, "__typename"),
        },
      },
      onCompleted: (_) => {
        const source = localStorage.getItem(KEYS.SOURCE);
        updateUrl({
          subscriptionId,
          url: get(
            data,
            "subscription.insurancePlan.config.generatedUrl",
            `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
          ),
        });

        if (!get(subscription, "subscription.locked"))
          return generateSubscriptionDocuments({
            onCompleted: (_) => {
              onNext({
                payload: {},
              });
            },
            variables: {
              data: { id: subscriptionId },
            },
          });

        onNext({
          payload: {},
        });
      },
    });
  };

  if (loading || isEmpty(initialValues)) return <Loader />;

  return (
    <>
      <Navbar
        project={project}
        progress={currentStatus?.progress}
        tracer={tracer}
        showCart
      />
      <View
        subscription={omitDeep(get(data, "subscription", {}), "__typename")}
        project={project}
        onNext={onSubmit}
        onBack={onBack}
        initialValues={initialValues}
        loading={generateLoading}
        showSepa={showSepa}
      />
    </>
  );
};

export default BankInformationFamilyCoverage;
